import { BudgetScenarioConsolidatedResult } from '@callmart/types';
import { extractBlobErrorMessage } from '../utils/fileUtils';
import { extractFileName } from '../utils/fetchUtils';
import api from './index';

const BASE = '/budget-scenarios';

// The selection the consolidated view (and its export) is built from: a baseline version plus the
// scenarios to overlay. Nothing is persisted — the page keeps this in its URL.
export interface ScenarioConsolidatedParams {
    versionId: number;
    scenarioIds: number[];
}

// Sent as a comma-separated list so the request URL stays readable and matches the page's ?scenarios=.
const toQuery = ({ versionId, scenarioIds }: ScenarioConsolidatedParams) => ({
    versionId,
    ...(scenarioIds.length > 0 ? { scenarioIds: scenarioIds.join(',') } : {}),
});

// CALL-765: the consolidated what-if view (Konsolidierte Ansicht) for one baseline version — its
// summary overlaid with the summed impacts of the selected scenarios. Read-only; never persists.
export const getScenarioConsolidated = async (
    params: ScenarioConsolidatedParams
): Promise<BudgetScenarioConsolidatedResult> => {
    return api.get(`${BASE}/consolidated`, { params: toQuery(params) }).then((res) => res.data);
};

// CALL-777: the scenarios workbook for one version — the scenario list, the consolidated board, and
// one sheet per scenario with its inputs and calculated impact. Server-built .xlsx.
//
// The selection goes along so the workbook's Konsolidiert sheet reproduces the board on screen rather
// than a differently-selected one.
//
// The filename comes from the response's Content-Disposition (the route sets it from the version NAME
// and exposes the header via Access-Control-Expose-Headers). Naming the file client-side instead would
// leave two exports as budget-szenarien-v12/v7.xlsx — indistinguishable without opening them.
export const exportBudgetScenarios = async (
    params: ScenarioConsolidatedParams
): Promise<{ data: Blob; fileName?: string }> => {
    try {
        const res = await api.get(`${BASE}/export`, { params: toQuery(params), responseType: 'blob' });
        return { data: res.data, fileName: extractFileName(res.headers) };
    } catch (error) {
        // An error response arrives as a Blob too, so recover the JSON message for the notification.
        const message = await extractBlobErrorMessage(error);
        if (message && error instanceof Error) error.message = message;
        throw error;
    }
};
